// Lib
import React, { useEffect, useRef, useState } from 'react';
import { Check, Plus, Settings } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Actions
import { SelectOrganization } from '@actions/organization';

// Services
import { applyActiveOrgToken } from '@services/sessionToken';

// UI
import { Avatar, AvatarFallback, AvatarImage } from '@components/ui/avatar';
import { Separator } from '@components/ui/separator';
import { cn } from '@components/lib/utils';
import OrganizationInvitationRows from './OrganizationInvitationRows';

// Types
import type { Organization } from '@app-types/index';
import type { PendingOrganizationInvitation } from '@hooks/useOrganizationInvitations';

/* Property switcher (top of the sidebar): the "where am I" scope. An
 * organization IS its property, so each row is one property (brand, show or
 * channel) — switch between them, add one, and jump into its settings.
 * Account-level actions live in the bottom anchor (`AccountPopover`), so the
 * two scopes never mix. */

interface OrgMenuProps {
  organizations?: Organization[];
  selectedOrganization?: Organization;
  onCreateOrganization: () => void;
  onClose: () => void;
  invitations?: PendingOrganizationInvitation[];
  busyInvitationId?: string | null;
  onAcceptInvitation: (invitationId: string) => Promise<Organization | null>;
  onDeclineInvitation: (invitationId: string) => Promise<boolean>;
}

/** Brand organizations are properties; creator organizations are not. */
export const newOrganizationLabel = (
  selected?: Pick<Organization, 'type'>,
): string =>
  selected?.type === 'creator' ? 'New organization' : 'New property';

const rowClass =
  'flex w-full items-center gap-2 rounded-lg p-1.5 text-left cursor-pointer hover:bg-marble-bg-50';

const initials = (name?: string): string =>
  (name ?? '').trim().charAt(0).toUpperCase() || '?';

const OrgMenu: React.FC<OrgMenuProps> = ({
  organizations,
  selectedOrganization,
  onCreateOrganization,
  onClose,
  invitations = [],
  busyInvitationId,
  onAcceptInvitation,
  onDeclineInvitation,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const activeId = selectedOrganization?._id;
  // Keep the active org pinned to the top so it's always visible (a long list
  // otherwise scrolls the current one out of view — you couldn't tell which
  // org you're in).
  const orgs = [...(organizations ?? [])].sort(
    (a, b) => Number(b?._id === activeId) - Number(a?._id === activeId),
  );

  // Bottom fade cue — signals the list scrolls when it doesn't all fit, and
  // disappears once scrolled to the end.
  const listRef = useRef<HTMLDivElement>(null);
  const [showFade, setShowFade] = useState(false);
  const updateFade = (): void => {
    const el = listRef.current;
    if (!el) return;
    setShowFade(el.scrollHeight - el.scrollTop - el.clientHeight > 4);
  };
  useEffect(updateFade, [organizations]);

  const onSelectOrganization = (organization: Organization): void => {
    dispatch(SelectOrganization(organization));
    if (organization?.token) applyActiveOrgToken(organization.token);
    navigate('/home', { state: { orgSwitchKey: organization?._id } });
    onClose();
  };

  return (
    <>
      {!!invitations.length && (
        <>
          <p className="flex items-center gap-1.5 px-1.5 pt-0.5 pb-1 text-xs font-semibold tracking-wide text-marble-bg-500 uppercase">
            Invitations
            <span className="rounded-full bg-marble-blue-100 px-1.5 text-[10px] font-semibold text-marble-blue-700 normal-case">
              {invitations.length}
            </span>
          </p>
          <OrganizationInvitationRows
            invitations={invitations}
            busyInvitationId={busyInvitationId}
            onAccept={onAcceptInvitation}
            onDecline={onDeclineInvitation}
          />
          <Separator className="my-1" />
        </>
      )}

      <p className="flex items-center gap-1.5 px-1.5 pt-0.5 pb-1 text-xs font-semibold tracking-wide text-marble-bg-500 uppercase">
        Your properties
        {orgs.length > 1 && (
          <span className="rounded-full bg-marble-bg-100 px-1.5 text-[10px] font-semibold text-marble-bg-600 normal-case">
            {orgs.length}
          </span>
        )}
      </p>

      <div className="relative">
        {/* ~5 rows visible, then scroll. */}
        <div
          ref={listRef}
          onScroll={updateFade}
          className="flex max-h-56 w-full flex-col gap-0.5 overflow-y-auto [scrollbar-width:thin]"
        >
          {orgs.map((organization) => {
            const isActive = organization?._id === activeId;
            return (
              <button
                type="button"
                key={organization?._id}
                aria-current={isActive ? 'true' : undefined}
                className={cn(rowClass, isActive && 'bg-marble-blue-50')}
                onClick={() => onSelectOrganization(organization)}
              >
                <Avatar size="sm">
                  <AvatarImage src={organization?.profile?.avatar?.url} />
                  <AvatarFallback>
                    {initials(organization?.profile?.name)}
                  </AvatarFallback>
                </Avatar>
                <p
                  className={cn(
                    'min-w-0 flex-1 truncate text-sm font-semibold',
                    isActive ? 'text-marble-blue-700' : 'text-marble-bg-700',
                  )}
                >
                  {organization?.profile?.name}
                </p>
                {isActive && (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-marble-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-marble-blue-700">
                    <Check className="size-3" />
                    Current
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {showFade && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-6 rounded-b bg-gradient-to-t from-popover to-transparent" />
        )}
      </div>

      <button
        type="button"
        className={rowClass}
        onClick={() => {
          onCreateOrganization();
          onClose();
        }}
      >
        <span className="flex size-6 shrink-0 items-center justify-center rounded-full border border-border bg-marble-bg-50">
          <Plus className="size-4 text-marble-bg-700" />
        </span>
        <p className="min-w-0 flex-1 truncate text-sm font-semibold text-marble-blue-500">
          {newOrganizationLabel(selectedOrganization)}
        </p>
      </button>

      <Separator className="my-1" />

      <button
        type="button"
        className={rowClass}
        onClick={() => {
          navigate('/settings/organization');
          onClose();
        }}
      >
        <Settings className="size-4 shrink-0 text-marble-bg-700" />
        <p className="text-sm font-semibold text-marble-bg-700">
          Property settings
        </p>
      </button>
    </>
  );
};

export default OrgMenu;
